import { type TResolution } from '@streaming/doppio-player';
import { EPlaybackState, EPlayerType } from '@streaming/mouflon-utils';

import { TPlayerError } from '@customTypes/common';
import { TStreamLoadErrorReason } from '@players/Players.events';

export enum EMouflonPlayerEvent {
  PLAYBACK_STATE_CHANGED = 'playbackStateChanged',
  VIDEO_PLAYBACK_STATE_CHANGED = 'videoPlaybackStateChanged',
  AVAILABLE_PLAYER_TYPES_UPDATED = 'availablePlayerModesUpdated',
  DEBUGGER_CONNECTED = 'debuggerConnected',
  ERROR = 'error',
  NATIVE_EVENT = 'nativeEvent',
  RESOLUTIONS_AVAILABLE = 'resolutionsAvailable',
  UPDATE_PLAYER_RESOLUTION = 'updatePlayerResolution',
  AUTO_RESOLUTION_SWITCHED = 'autoResolutionSwitched',
  RESOLUTION_SWITCHED = 'resolutionSwitched',
  TIME_UPDATE = 'timeUpdate',
  MODULE_LOADING_STATE_CHANGED = 'moduleLoadingStateChanged',
  PLAYING = 'playing',
  RESOLUTION_NOT_FOUND = 'resolutionNotFound',
  BROADCAST_SUPPORTED_CHANGE = 'broadcastSupportedChange',
  STREAM_LOAD_ERROR = 'streamLoadError',
}

type TNativeVideoElementEventKey = keyof HTMLVideoElementEventMap;

type TNativeVideoElementEvent = HTMLVideoElementEventMap[TNativeVideoElementEventKey];

export enum EModuleLoadingState {
  LOADING = 'loading',
  LOADED = 'loaded',
}

export type TMouflonPlayerEventsMap = {
  [EMouflonPlayerEvent.AVAILABLE_PLAYER_TYPES_UPDATED]: CustomEvent<{
    availablePlayerTypes: EPlayerType[];
  }>;
  [EMouflonPlayerEvent.PLAYBACK_STATE_CHANGED]: CustomEvent<{
    prevState: EPlaybackState;
    nextState: EPlaybackState;
  }>;
  [EMouflonPlayerEvent.VIDEO_PLAYBACK_STATE_CHANGED]: CustomEvent<{
    prevState: EPlaybackState;
    nextState: EPlaybackState;
  }>;
  [EMouflonPlayerEvent.DEBUGGER_CONNECTED]: CustomEvent<{
    debuggerUrl: string;
  }>;

  [EMouflonPlayerEvent.ERROR]: CustomEvent<TPlayerError>;

  [EMouflonPlayerEvent.NATIVE_EVENT]: CustomEvent<{
    event: TNativeVideoElementEvent;
    eventName: TNativeVideoElementEventKey;
  }>;

  [EMouflonPlayerEvent.RESOLUTIONS_AVAILABLE]: CustomEvent<{
    resolutions: TResolution[];
  }>;

  [EMouflonPlayerEvent.UPDATE_PLAYER_RESOLUTION]: CustomEvent<{
    height: number;
    width: number;
  }>;

  [EMouflonPlayerEvent.AUTO_RESOLUTION_SWITCHED]: CustomEvent<{
    name: string;
  }>;

  [EMouflonPlayerEvent.TIME_UPDATE]: CustomEvent<Record<string, never>>;
  [EMouflonPlayerEvent.MODULE_LOADING_STATE_CHANGED]: CustomEvent<{
    newState: EModuleLoadingState;
  }>;
  [EMouflonPlayerEvent.RESOLUTION_SWITCHED]: CustomEvent<{
    resolutionName: string;
  }>;
  [EMouflonPlayerEvent.PLAYING]: CustomEvent<{
    isAutoplay?: boolean;
  }>;
  [EMouflonPlayerEvent.RESOLUTION_NOT_FOUND]: CustomEvent<unknown>;
  [EMouflonPlayerEvent.BROADCAST_SUPPORTED_CHANGE]: CustomEvent<{ isSupported: boolean }>;
  [EMouflonPlayerEvent.STREAM_LOAD_ERROR]: CustomEvent<{
    reason: TStreamLoadErrorReason;
    httpStatusCode?: number;
    wsStatusCode?: number;
    signallingMessage?: string;
  }>;
};

export type TMouflonPlayerEventPayload<T extends keyof TMouflonPlayerEventsMap> =
  TMouflonPlayerEventsMap[T];
